import React from 'react';
import DrawerShell from '@/components/DrawerShell';
import { Button } from '@/components/ui/button';
import { Calendar, FileBarChart, FileDown, Pill, ScrollText, Users } from 'lucide-react';

export default function ManagementDrillDrawer({
  drawer, onClose, workload, referrals, schedule, roster, downloadPdf,
}) {
  if (!drawer) return null;
  const k = drawer.kind;

  if (k === 'doctors') {
    return (
      <DrawerShell open onClose={onClose} eyebrow="Workload" title="Doctor Leaderboard"
        subtitle="Ranked by visits in selected period" accent="#F15A22" icon={<Users className="h-5 w-5" />}
        width="xl" testId="mgmt-drawer-doctors">
        <div className="p-5 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="text-left py-2">#</th><th className="text-left">Doctor</th>
                <th className="text-left">Specialty</th><th className="text-right">Visits</th>
                <th className="text-right">Rx</th><th className="text-right">Hours</th><th className="text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {(workload?.doctors || []).map((d, i) => (
                <tr key={d.doctor_id} className="border-t border-zinc-100">
                  <td className="py-2 text-zinc-400">{i + 1}</td>
                  <td className="font-semibold">Dr. {d.name}</td>
                  <td className="text-zinc-600">{d.specialization || '—'}</td>
                  <td className="text-right font-mono">{d.visits}</td>
                  <td className="text-right font-mono">{d.rx_lines}</td>
                  <td className="text-right font-mono">{d.hours_rostered.toFixed(1)}</td>
                  <td className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf('doctor', d.doctor_id)}>
                      <FileDown className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DrawerShell>
    );
  }

  if (k === 'referrals') {
    return (
      <DrawerShell open onClose={onClose} eyebrow="Care Pathway" title="Referrals"
        subtitle={`${referrals?.total ?? 0} in this period`} accent="#7c3aed" icon={<ScrollText className="h-5 w-5" />}
        width="lg" testId="mgmt-drawer-referrals">
        <div className="p-5 space-y-3">
          {(referrals?.items || []).length === 0 ? (
            <p className="text-sm text-zinc-500">No referrals captured yet.</p>
          ) : referrals.items.map((r, i) => (
            <div key={i} className="border rounded-xl p-3.5">
              <div className="flex justify-between text-xs">
                <span className="font-bold">Dr. {r.doctor_name}</span>
                <span className="text-zinc-500">
                  {r.at ? new Date(r.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}
                </span>
              </div>
              <div className="text-sm mt-1">→ <b className="text-violet-700">{r.referred_to}</b></div>
              <div className="text-xs text-zinc-600 mt-1">Reason: {r.reason}</div>
            </div>
          ))}
        </div>
      </DrawerShell>
    );
  }

  if (k === 'schedule') {
    return (
      <DrawerShell open onClose={onClose} eyebrow="Pharmacovigilance" title="Schedule-drug Rx"
        subtitle={`${schedule?.total ?? 0} lines · H / H1 / X`} accent="#dc2626" icon={<Pill className="h-5 w-5" />}
        width="lg" testId="mgmt-drawer-schedule">
        <div className="p-5 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="text-left py-2">When</th><th className="text-left">Doctor</th>
                <th className="text-left">Medicine</th><th className="text-left">Sch</th>
              </tr>
            </thead>
            <tbody>
              {(schedule?.items || []).map((r, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="py-2 text-zinc-700">
                    {r.at ? new Date(r.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}
                  </td>
                  <td className="font-semibold">Dr. {r.doctor_name}</td>
                  <td>{r.medicine}</td>
                  <td><span className="text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 rounded px-1.5">Sch {r.schedule}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DrawerShell>
    );
  }

  if (k === 'roster') {
    return (
      <DrawerShell open onClose={onClose} eyebrow="Operations" title="Roster window"
        subtitle={`${roster?.from} → ${roster?.to}`} accent="#059669" icon={<Calendar className="h-5 w-5" />}
        width="lg" testId="mgmt-drawer-roster">
        <div className="p-5 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="text-left py-2">Date</th><th className="text-left">Staff</th>
                <th className="text-left">Start</th><th className="text-left">End</th>
              </tr>
            </thead>
            <tbody>
              {(roster?.shifts || []).map((s, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="py-2">{s.date}</td>
                  <td className="font-semibold">{s.doctor}</td>
                  <td>{s.start}</td>
                  <td>{s.end}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DrawerShell>
    );
  }

  return (
    <DrawerShell open onClose={onClose} eyebrow="Detail" title="Period summary"
      accent="#0891b2" icon={<FileBarChart className="h-5 w-5" />} width="md" testId={`mgmt-drawer-${k}`}>
      <div className="p-5 text-sm space-y-2 text-zinc-700">
        <p>Aggregations use the Asia/Kolkata window from the filter bar.</p>
        <p>Use Load & capacity and Ask AI for actionable breakdowns.</p>
      </div>
    </DrawerShell>
  );
}
